import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  ActivityIndicator,
  useTheme,
  Surface,
  Dialog,
  Portal,
  Button,
  TextInput,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { MessageSquare, ChevronRight } from 'lucide-react-native';
import {
  useUserComments,
  useDeleteComment,
  useUpdateComment,
} from '@coffeeplexity/api-client';
import { useAppSelector } from '../store';
import { CommentItem } from '../components/comments';

export function MyCommentsScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const { user } = useAppSelector(state => state.auth);

  const { data, isLoading, refetch, isRefetching } = useUserComments();
  const deleteComment = useDeleteComment();
  const updateComment = useUpdateComment();

  const comments = data?.comments || [];

  // Edit dialog state
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editingComment, setEditingComment] = useState<any>(null);
  const [editText, setEditText] = useState('');

  const handleNavigateToRecipe = useCallback(
    (recipeId: string) => {
      (navigation as any).navigate('recipeDetail', { recipeId });
    },
    [navigation]
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      try {
        await deleteComment.mutateAsync(commentId);
        Toast.show({
          type: 'success',
          text1: 'Comment deleted',
        });
      } catch (error) {
        Toast.show({
          type: 'error',
          text1: 'Failed to delete comment',
          text2: 'Please try again',
        });
      }
    },
    [deleteComment]
  );

  const handleEditComment = useCallback((comment: any) => {
    setEditingComment(comment);
    setEditText(comment.text || comment.content || '');
    setEditDialogVisible(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingComment || !editText.trim()) {
      Toast.show({
        type: 'error',
        text1: 'Comment cannot be empty',
      });
      return;
    }

    try {
      await updateComment.mutateAsync({
        commentId: editingComment.id,
        content: editText.trim(),
      });

      setEditDialogVisible(false);
      setEditingComment(null);
      setEditText('');

      Toast.show({
        type: 'success',
        text1: 'Comment updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update comment',
        text2: 'Please try again',
      });
    }
  }, [editingComment, editText, updateComment]);

  const handleCancelEdit = useCallback(() => {
    setEditDialogVisible(false);
    setEditingComment(null);
    setEditText('');
  }, []);

  const renderCommentItem = useCallback(
    ({ item }: { item: any }) => (
      <Surface
        style={[styles.commentCard, { backgroundColor: theme.colors.surface }]}
        elevation={0}
      >
        {/* Recipe Context Header */}
        {item.content_title && item.content_id && (
          <TouchableOpacity
            style={styles.recipeHeader}
            onPress={() => handleNavigateToRecipe(item.content_id)}
            activeOpacity={0.7}
          >
            <View style={styles.recipeHeaderContent}>
              <Text
                variant='labelSmall'
                style={[
                  styles.recipeLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Commented on
              </Text>
              <Text
                variant='titleSmall'
                style={[styles.recipeTitle, { color: theme.colors.onSurface }]}
                numberOfLines={1}
              >
                {item.content_title}
              </Text>
            </View>
            <ChevronRight
              size={16}
              color={theme.colors.onSurfaceVariant}
              strokeWidth={1.5}
            />
          </TouchableOpacity>
        )}

        {/* Comment Content */}
        <View style={styles.commentContent}>
          <CommentItem
            comment={item}
            currentUserId={user?.id}
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
            hideLikeButton={true}
          />
        </View>
      </Surface>
    ),
    [
      theme,
      user,
      handleNavigateToRecipe,
      handleEditComment,
      handleDeleteComment,
    ]
  );

  // Note: Comments are displayed in chronological order
  // Future enhancement: Group by recipe if needed

  if (isLoading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.centerContainer}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text
            variant='bodyMedium'
            style={[
              styles.loadingText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Loading your comments...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      edges={['top']}
    >
      <FlatList
        data={comments}
        keyExtractor={item => item.id}
        renderItem={renderCommentItem}
        contentContainerStyle={styles.listContent}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        windowSize={10}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        ListHeaderComponent={
          comments.length > 0 ? (
            <View style={styles.headerContainer}>
              <Text
                variant='labelMedium'
                style={[
                  styles.sectionHeader,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                RECIPE COMMENTS
              </Text>
            </View>
          ) : null
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <View
              style={[
                styles.emptyIconContainer,
                { backgroundColor: theme.colors.surfaceVariant },
              ]}
            >
              <MessageSquare
                size={48}
                color={theme.colors.onSurfaceVariant}
                strokeWidth={1.5}
              />
            </View>
            <Text
              variant='titleLarge'
              style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
            >
              No comments yet
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Your comments on recipes will appear here
            </Text>
          </View>
        }
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={refetch}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
      />

      {/* Edit Comment Dialog */}
      <Portal>
        <Dialog
          visible={editDialogVisible}
          onDismiss={handleCancelEdit}
          style={{ backgroundColor: theme.colors.surface }}
        >
          <Dialog.Title>Edit Comment</Dialog.Title>
          <Dialog.Content>
            <TextInput
              mode='outlined'
              value={editText}
              onChangeText={setEditText}
              multiline
              numberOfLines={4}
              placeholder='Edit your comment...'
              style={styles.editInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={handleCancelEdit}>Cancel</Button>
            <Button
              onPress={handleSaveEdit}
              loading={updateComment.isPending}
              disabled={!editText.trim() || updateComment.isPending}
            >
              Save
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 40,
  },
  headerContainer: {
    paddingTop: 8,
    paddingBottom: 16,
    paddingHorizontal: 4,
  },
  sectionHeader: {
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontSize: 11,
    fontWeight: '600',
    opacity: 0.7,
  },
  commentCard: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  recipeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  recipeHeaderContent: {
    flex: 1,
    gap: 2,
  },
  recipeLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: '500',
    opacity: 0.6,
  },
  recipeTitle: {
    fontWeight: '600',
    letterSpacing: -0.2,
  },
  commentContent: {
    padding: 16,
  },
  separator: {
    height: 12,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingTop: 120,
  },
  emptyIconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  emptyTitle: {
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
    letterSpacing: -0.5,
  },
  emptySubtitle: {
    textAlign: 'center',
    opacity: 0.8,
    lineHeight: 20,
  },
  editInput: {
    minHeight: 100,
  },
});
